import type { ReactNode } from 'react';

export interface HelpTopic {
  id: string;
  icon: string;
  title: string;
  summary: string;
  body: ReactNode;
}

function P({ children }: { children: ReactNode }) {
  return <p className="text-sm leading-relaxed text-slate-700">{children}</p>;
}

function Sub({ children }: { children: ReactNode }) {
  return <h3 className="mt-4 text-sm font-semibold text-slate-900">{children}</h3>;
}

function Ex({ children }: { children: ReactNode }) {
  return (
    <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
      💬 {children}
    </p>
  );
}

export const HELP_TOPICS: HelpTopic[] = [
  {
    id: 'comecando',
    icon: '🚀',
    title: 'Começando do zero',
    summary: 'A ordem certa pra configurar o app pela primeira vez.',
    body: (
      <div className="flex flex-col gap-3">
        <P>
          O app organiza o dinheiro da casa em volta de <strong>contas</strong> (onde o dinheiro
          está) e <strong>lançamentos</strong> (o que entra e o que sai). A ordem que funciona
          melhor:
        </P>
        <P>
          1️⃣ Crie suas <strong>contas</strong> em "Contas" — conta corrente, poupança, carteira
          (dinheiro vivo), vale-refeição… Informe o saldo que cada uma tem hoje ("saldo inicial").
          <br />
          2️⃣ Cadastre seus <strong>cartões de crédito</strong> em "Cartões", com dia de fechamento e
          vencimento.
          <br />
          3️⃣ Registre os gastos e receitas do dia a dia em <strong>"Transações"</strong>.
          <br />
          4️⃣ Coloque as contas fixas (aluguel, luz, Netflix, salário) em{' '}
          <strong>"Recorrências"</strong> — o app passa a lançá-las sozinho.
        </P>
        <Ex>
          Não precisa cadastrar o passado: comece com os saldos de hoje e registre daqui pra frente.
        </Ex>
      </div>
    ),
  },
  {
    id: 'dashboard',
    icon: '◐',
    title: 'Dashboard (visão geral)',
    summary: 'O que significam os números da tela inicial.',
    body: (
      <div className="flex flex-col gap-3">
        <P>
          O Dashboard resume o mês atual: <strong>saldo total</strong> (soma de todas as contas),{' '}
          <strong>receitas</strong> e <strong>despesas</strong> do mês e o{' '}
          <strong>resultado</strong> (receitas − despesas).
        </P>
        <Sub>Por que transferências não aparecem como despesa?</Sub>
        <P>
          Mover dinheiro da conta corrente pra poupança não é gasto — o dinheiro continua seu. Por
          isso transferências mudam os saldos das contas, mas não entram em receitas/despesas.
        </P>
        <Ex>
          Se o "resultado (mês)" está negativo, você gastou mais do que ganhou neste mês — vale
          olhar as maiores categorias de despesa logo abaixo.
        </Ex>
      </div>
    ),
  },
  {
    id: 'contas',
    icon: '🏦',
    title: 'Contas e carteiras',
    summary: 'Onde o dinheiro está: banco, poupança, dinheiro vivo, vales.',
    body: (
      <div className="flex flex-col gap-3">
        <P>
          Cada <strong>conta</strong> representa um lugar onde existe dinheiro: conta corrente,
          poupança, corretora, a carteira física no bolso, vale-refeição/alimentação…
        </P>
        <Sub>Saldo inicial</Sub>
        <P>
          É quanto a conta tinha <em>no momento em que você a cadastrou</em>. A partir daí o app
          calcula o saldo sozinho: saldo inicial + tudo que entrou − tudo que saiu.
        </P>
        <Sub>Métodos aceitos</Sub>
        <P>
          Cada conta aceita certos meios de pagamento (Pix, débito, dinheiro…). É isso que evita
          erros como "paguei com Pix da carteira de dinheiro vivo". Dá pra ajustar os métodos de
          cada conta ao editá-la.
        </P>
        <Ex>
          A carteira física só aceita "Dinheiro". Se um gasto foi no Pix, ele saiu de uma conta de
          banco — o app te avisa se você tentar o contrário.
        </Ex>
      </div>
    ),
  },
  {
    id: 'transacoes',
    icon: '↕',
    title: 'Transações (lançamentos)',
    summary: 'Registrar gastos, receitas e transferências.',
    body: (
      <div className="flex flex-col gap-3">
        <P>
          Todo dinheiro que entra ou sai é um <strong>lançamento</strong>. Ao criar um, você diz:
          foi <strong>despesa ou receita</strong>, <strong>como pagou</strong> (Pix, débito,
          crédito…), <strong>de qual conta</strong> saiu/entrou, o valor e a data.
        </P>
        <Sub>Status: pendente × efetivado</Sub>
        <P>
          <strong>Efetivado</strong> = já aconteceu, conta no saldo. <strong>Pendente</strong> =
          previsto, ainda não confirmado — é como as recorrências entram quando o "lançar sozinho"
          está desligado, esperando seu ok.
        </P>
        <Sub>Transferências</Sub>
        <P>
          Use "transferir" para mover dinheiro entre suas contas (corrente → poupança). O app cria
          as duas pontas de uma vez e não conta isso como gasto.
        </P>
        <Sub>De onde veio este lançamento?</Sub>
        <P>
          Cada lançamento guarda a origem: <em>manual</em> (você criou), <em>recorrente</em> (veio
          de uma recorrência), <em>fatura</em> (pagamento de cartão) ou <em>transferência</em>. Dá
          pra filtrar por origem na lista.
        </P>
      </div>
    ),
  },
  {
    id: 'cartoes',
    icon: '💳',
    title: 'Cartões de crédito e faturas',
    summary: 'Fechamento, vencimento e por que a compra "não sai" da conta na hora.',
    body: (
      <div className="flex flex-col gap-3">
        <P>
          Compra no <strong>crédito</strong> não sai da sua conta na hora — ela entra na{' '}
          <strong>fatura</strong> do cartão. Quem sai da conta é o{' '}
          <strong>pagamento da fatura</strong>, uma vez por mês.
        </P>
        <Sub>Fechamento × vencimento</Sub>
        <P>
          <strong>Fechamento</strong> é quando a fatura "congela": compras depois dele caem na
          fatura seguinte. <strong>Vencimento</strong> é o dia de pagar. Alguns bancos fecham num
          dia fixo (Itaú, BB); outros fecham X dias antes do vencimento (Nubank, Inter) — o app
          suporta os dois modos.
        </P>
        <Sub>Pagar a fatura</Sub>
        <P>
          Na tela da fatura, use "pagar" escolhendo de qual conta sai o dinheiro. O app cria o
          lançamento de pagamento e marca a fatura como paga.
        </P>
        <Ex>
          Cartão que fecha dia 25 e vence dia 5: uma compra no dia 26 só aparece na fatura que vence
          dia 5 do <em>mês seguinte</em>.
        </Ex>
      </div>
    ),
  },
  {
    id: 'recorrencias',
    icon: '🔁',
    title: 'Recorrências e assinaturas',
    summary: 'Contas fixas que o app lança sozinho: aluguel, Netflix, salário…',
    body: (
      <div className="flex flex-col gap-3">
        <P>
          Uma <strong>recorrência</strong> é um lançamento que se repete: todo mês, toda semana,
          todo ano. Você cadastra uma vez e o app cria os lançamentos na data certa — de madrugada,
          automaticamente.
        </P>
        <Sub>"Lançar sozinho" ligado ou desligado?</Sub>
        <P>
          <strong>Desligado</strong> (padrão): o lançamento entra como <em>pendente</em> e você
          confirma em Transações — bom pra contas de valor variável (luz, água).{' '}
          <strong>Ligado</strong>: entra direto como efetivado — bom pra valores fixos (aluguel,
          assinatura).
        </P>
        <Sub>Dia 31 em mês curto?</Sub>
        <P>Cai no último dia do mês (31 → 28/29 em fevereiro) e volta ao normal depois.</P>
        <Sub>Pausar, encerrar, lançar agora</Sub>
        <P>
          "Pausar" segura a recorrência sem apagar o histórico. "Termina em" encerra sozinha numa
          data. "Lançar agora" cria o próximo lançamento imediatamente, sem esperar a data.
        </P>
        <Ex>
          Netflix R$ 55,90 no crédito, todo dia 10, "lançar sozinho" ligado: a compra entra na
          fatura do cartão certinha todo mês, sem você fazer nada.
        </Ex>
      </div>
    ),
  },
  {
    id: 'categorias',
    icon: '🏷',
    title: 'Categorias',
    summary: 'Agrupar gastos pra saber pra onde o dinheiro vai.',
    body: (
      <div className="flex flex-col gap-3">
        <P>
          Categorias respondem "com o que eu gasto?": Mercado, Transporte, Lazer, Salário… O app já
          vem com um conjunto pronto e você pode criar as suas.
        </P>
        <P>
          Categorizar não é obrigatório, mas é o que faz o Dashboard mostrar as maiores despesas do
          mês por grupo — vale os 2 segundos ao lançar.
        </P>
        <Ex>
          Na dúvida entre criar categoria nova ou usar uma existente, use a existente: menos
          categorias = relatórios mais claros.
        </Ex>
      </div>
    ),
  },
  {
    id: 'compartilhado',
    icon: '👥',
    title: 'Pessoal × compartilhado (household)',
    summary: 'Como funciona o espaço da casa e o que cada um enxerga.',
    body: (
      <div className="flex flex-col gap-3">
        <P>
          O <strong>household</strong> é o espaço da casa: todos os membros veem as contas e
          lançamentos marcados como <strong>compartilhados</strong>. O que não for marcado é
          pessoal.
        </P>
        <Sub>Papéis</Sub>
        <P>
          O <strong>dono</strong> (owner) administra: convida membros, remove, gera link de
          redefinição de senha. <strong>Membros</strong> usam normalmente, mas só editam o que é
          deles.
        </P>
        <Sub>Convidar alguém</Sub>
        <P>
          O dono gera um convite (link) e envia. A pessoa cria a própria senha ao aceitar — senhas
          nunca são compartilhadas.
        </P>
      </div>
    ),
  },
  {
    id: 'seguranca',
    icon: '🔐',
    title: 'Senha e segurança',
    summary: 'Trocar senha, esqueci a senha, e como seus dados são protegidos.',
    body: (
      <div className="flex flex-col gap-3">
        <Sub>Trocar a senha (lembrando a atual)</Sub>
        <P>Menu da conta (canto superior direito) → "trocar senha".</P>
        <Sub>Esqueci a senha</Sub>
        <P>
          Peça ao <strong>dono do household</strong>: no menu da conta ele tem "redefinir senha de
          membro", que gera um link de uso único válido por 30 minutos. Abrindo o link, você define
          a senha nova na hora.
        </P>
        <Sub>Boas práticas</Sub>
        <P>
          Senhas têm no mínimo 12 caracteres com letra e número. Ao trocar ou redefinir a senha,
          todos os aparelhos conectados são desconectados por segurança. O app nunca mostra sua
          senha a ninguém — nem ao dono.
        </P>
      </div>
    ),
  },
  {
    id: 'instituicoes',
    icon: '🏢',
    title: 'Instituições',
    summary: 'Bancos, corretoras e fintechs — o "de quem é" cada conta.',
    body: (
      <div className="flex flex-col gap-3">
        <P>
          Instituições são os bancos/corretoras por trás das contas (Itaú, Nubank, XP…). Servem pra
          organizar: ao criar uma conta você pode dizer de qual instituição ela é.
        </P>
        <Sub>Valem para a casa toda</Sub>
        <P>
          Diferente das contas, instituições <strong>não têm dono</strong> — um único "Inter"
          cadastrado serve para as contas de todo mundo. Não crie "Inter" e "Inter da Arieli": quem
          diz de quem é a conta é a <em>própria conta</em>, não o banco.
        </P>
        <Ex>
          Casal com conta no mesmo banco = 1 instituição ("Sicredi") e 2 contas ("Sicredi Gabriel",
          "Sicredi Ari"), cada uma com seu dono.
        </Ex>
      </div>
    ),
  },
];

export const HELP_BY_ID: Record<string, HelpTopic> = Object.fromEntries(
  HELP_TOPICS.map((t) => [t.id, t]),
);
